/**
 * "Live now" on Home (collaboration phase 2, design board "Home with live presence"): the projects shared with you (or
 * yours, shared with others) where someone else or an AI agent is active right now, from `GET /api/v1/live` (polled
 * every 20 s while the page is visible, shared with the sidebar; the gateway only asks sandboxes that are awake, so this never wakes one).
 * Renders nothing when nobody is around, or where the endpoint does not exist.
 */
import React from 'react';
import PropTypes from 'prop-types';
import useLiveNow from '../../hooks/useLiveNow';
import { liveRows, liveSummary } from '../../utils/liveModel';
import { plural } from '../../utils/changesModel';
import { ParticipantMark } from '../presence/Presence';
import './Collab.css';

export default function LiveNow({ onOpenProject, you = null }) {
  // One shared poll for the page (hooks/useLiveNow.js): the sidebar's LIVE NOW list reads the same answer.
  const answer = useLiveNow();
  const { projects, people, agents } = liveSummary(answer);
  if (projects.length === 0) { return null; }
  return (
    <section aria-labelledby="collab-live-title" className="collab-live" data-testid="live-now">
      <div className="collab-live-head">
        <h2 id="collab-live-title"><span aria-hidden="true" className="collab-live-now-dot" />Happening now in shared projects</h2>
        <span className="collab-muted collab-small">
          {[people ? plural(people, 'person').replace('persons', 'people') : null, agents ? plural(agents, 'AI agent') : null].filter(Boolean).join(' · ')}
        </span>
      </div>
      {/* One block per project: its name and who shared it, Open on the right, then who is there, indented under it. */}
      {projects.map((project) => (
        <div className="collab-live-project" data-testid="live-now-project" key={project.projectId}>
          <div className="collab-live-project-head">
            <span className="collab-live-project-name">
              <strong>{project.name || project.projectId}</strong>
              <span className="collab-muted collab-small">
                {project.role === 'owner' ? 'You own it' : project.owner?.name ? `Shared by ${project.owner.name}` : ''}
              </span>
            </span>
            <button className="collab-link collab-live-open" onClick={() => onOpenProject(project.projectId)} type="button">Open</button>
          </div>
          <ul className="collab-live-people">
            {liveRows(project, you).map((row) => (
              <li className="collab-live-person" key={row.key}>
                {row.kind === 'agent'
                  ? <ParticipantMark agent initials={row.mark.driver?.initials || 'AI'} paused={Boolean(row.mark.paused)} size={22} slot={row.mark.slot} />
                  : <ParticipantMark idle={row.mark.state === 'idle'} initials={row.mark.initials} size={22} slot={row.mark.slot} />}
                <span>{row.text}{row.detail ? <span className="collab-muted collab-small"> · {row.detail}</span> : null}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}

LiveNow.propTypes = {
  onOpenProject: PropTypes.func.isRequired,
  you: PropTypes.object,
};
